/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    XMarkIcon,
    KeyIcon,
    PlusIcon,
    ExclamationTriangleIcon,
    ClipboardIcon,
    CheckIcon,
    ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { sshKeyService } from '../../services/api';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import toast from 'react-hot-toast';
import { SSHKey, SSHKeyType, GeneratedSSHKey } from '../../types';

interface CreateSSHKeyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onKeyCreated?: (key: SSHKey) => void;
}

type ModalMode = 'select' | 'add' | 'generate' | 'save-private-key';

const CreateSSHKeyModal: React.FC<CreateSSHKeyModalProps> = ({
    isOpen,
    onClose,
    onKeyCreated,
}) => {
    const queryClient = useQueryClient();
    const { selectedProjectId } = useSelectedProject();
    const [mode, setMode] = useState<ModalMode>('select');

    // Add key form state
    const [addFormData, setAddFormData] = useState({
        name: '',
        public_key: '',
    });

    // Generate key form state
    const [generateFormData, setGenerateFormData] = useState({
        name: '',
        key_type: 'ed25519' as SSHKeyType,
        key_size: 4096,
    });

    // Generated key display state
    const [generatedKey, setGeneratedKey] = useState<GeneratedSSHKey | null>(null);
    const [privateKeyCopied, setPrivateKeyCopied] = useState(false);

    // Add SSH key mutation
    const addMutation = useMutation({
        mutationFn: (data: { name: string; publicKey: string }) =>
            sshKeyService.create(data.name, data.publicKey, selectedProjectId || undefined),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
            toast.success('SSH key added successfully');
            const key = response.data?.data || response.data;
            if (onKeyCreated && key) {
                onKeyCreated(key);
            }
            handleClose();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to add SSH key');
        },
    });

    // Generate SSH key mutation
    const generateMutation = useMutation({
        mutationFn: (data: { name: string; keyType: SSHKeyType; keySize?: number }) =>
            sshKeyService.generate(data.name, data.keyType, data.keySize, selectedProjectId || undefined),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
            const key = response.data?.data || response.data;
            setGeneratedKey(key);
            setMode('save-private-key');
            toast.success('SSH key generated successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to generate SSH key');
        },
    });

    const handleClose = () => {
        setMode('select');
        setAddFormData({ name: '', public_key: '' });
        setGenerateFormData({ name: '', key_type: 'ed25519', key_size: 4096 });
        setGeneratedKey(null);
        setPrivateKeyCopied(false);
        onClose();
    };

    const handleAddKey = (e: React.FormEvent) => {
        e.preventDefault();
        addMutation.mutate({
            name: addFormData.name,
            publicKey: addFormData.public_key,
        });
    };

    const handleGenerateKey = (e: React.FormEvent) => {
        e.preventDefault();
        generateMutation.mutate({
            name: generateFormData.name,
            keyType: generateFormData.key_type,
            keySize: generateFormData.key_type === 'rsa' ? generateFormData.key_size : undefined,
        });
    };

    const copyPrivateKey = async () => {
        if (generatedKey?.private_key) {
            try {
                await navigator.clipboard.writeText(generatedKey.private_key);
                setPrivateKeyCopied(true);
                toast.success('Private key copied to clipboard');
                setTimeout(() => setPrivateKeyCopied(false), 2000);
            } catch {
                toast.error('Failed to copy');
            }
        }
    };

    const downloadPrivateKey = () => {
        if (generatedKey?.private_key) {
            const blob = new Blob([generatedKey.private_key], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${generatedKey.name.replace(/\s+/g, '_')}.pem`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Private key downloaded');
        }
    };

    const handleSaveAndClose = () => {
        if (generatedKey && onKeyCreated) {
            // GeneratedSSHKey extends SSHKey, so we can pass it directly
            // Just exclude the private_key field
            const { private_key, ...sshKey } = generatedKey;
            onKeyCreated(sshKey as SSHKey);
        }
        handleClose();
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-500/20 rounded-lg">
                            <KeyIcon className="w-6 h-6 text-primary-500" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">
                                {mode === 'select' && 'Add SSH Key'}
                                {mode === 'add' && 'Add Existing Key'}
                                {mode === 'generate' && 'Generate New Key'}
                                {mode === 'save-private-key' && 'Save Your Private Key'}
                            </h3>
                            <p className="text-sm text-gray-400">
                                {mode === 'select' && 'Choose how to add your SSH key'}
                                {mode === 'add' && 'Paste your existing public key'}
                                {mode === 'generate' && 'Generate a new key pair'}
                                {mode === 'save-private-key' && 'Download before closing'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded-lg"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Mode Selection */}
                    {mode === 'select' && (
                        <div className="space-y-3">
                            <button
                                onClick={() => setMode('add')}
                                className="w-full flex items-center gap-4 p-4 border-2 border-gray-700 rounded-xl hover:border-primary-500 hover:bg-primary-500/10 transition-colors text-left"
                            >
                                <div className="p-3 bg-gray-800 rounded-lg">
                                    <PlusIcon className="w-6 h-6 text-gray-400" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-white">Add Existing Key</h4>
                                    <p className="text-sm text-gray-400">
                                        Paste a public key from your computer
                                    </p>
                                </div>
                            </button>
                            <button
                                onClick={() => setMode('generate')}
                                className="w-full flex items-center gap-4 p-4 border-2 border-gray-700 rounded-xl hover:border-primary-500 hover:bg-primary-500/10 transition-colors text-left"
                            >
                                <div className="p-3 bg-primary-500/20 rounded-lg">
                                    <KeyIcon className="w-6 h-6 text-primary-500" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-white">Generate New Key</h4>
                                    <p className="text-sm text-gray-400">
                                        Create a new SSH key pair (recommended)
                                    </p>
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Add Existing Key Form */}
                    {mode === 'add' && (
                        <form onSubmit={handleAddKey} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Key Name
                                </label>
                                <input
                                    type="text"
                                    value={addFormData.name}
                                    onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                                    className="input"
                                    placeholder="e.g., My Laptop"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Public Key
                                </label>
                                <textarea
                                    value={addFormData.public_key}
                                    onChange={(e) => setAddFormData({ ...addFormData, public_key: e.target.value })}
                                    className="input resize-none font-mono text-sm"
                                    rows={4}
                                    placeholder="ssh-rsa AAAA... or ssh-ed25519 AAAA..."
                                    required
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                    Usually found in ~/.ssh/id_rsa.pub or ~/.ssh/id_ed25519.pub
                                </p>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setMode('select')}
                                    className="flex-1 btn-secondary"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={addMutation.isPending}
                                    className="flex-1 btn-primary disabled:opacity-50"
                                >
                                    {addMutation.isPending ? 'Adding...' : 'Add Key'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Generate Key Form */}
                    {mode === 'generate' && (
                        <form onSubmit={handleGenerateKey} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Key Name
                                </label>
                                <input
                                    type="text"
                                    value={generateFormData.name}
                                    onChange={(e) => setGenerateFormData({ ...generateFormData, name: e.target.value })}
                                    className="input"
                                    placeholder="e.g., Production Key"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Key Type
                                </label>
                                <select
                                    value={generateFormData.key_type}
                                    onChange={(e) => setGenerateFormData({
                                        ...generateFormData,
                                        key_type: e.target.value as SSHKeyType
                                    })}
                                    className="input"
                                >
                                    <option value="ed25519">ED25519 (Recommended)</option>
                                    <option value="rsa">RSA</option>
                                </select>
                                <p className="mt-1 text-xs text-gray-400">
                                    ED25519 is faster and more secure. RSA is more compatible with older systems.
                                </p>
                            </div>
                            {generateFormData.key_type === 'rsa' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Key Size
                                    </label>
                                    <select
                                        value={generateFormData.key_size}
                                        onChange={(e) => setGenerateFormData({
                                            ...generateFormData,
                                            key_size: parseInt(e.target.value)
                                        })}
                                        className="input"
                                    >
                                        <option value={2048}>2048 bits</option>
                                        <option value={4096}>4096 bits (Recommended)</option>
                                    </select>
                                </div>
                            )}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setMode('select')}
                                    className="flex-1 btn-secondary"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={generateMutation.isPending}
                                    className="flex-1 btn-primary disabled:opacity-50"
                                >
                                    {generateMutation.isPending ? 'Generating...' : 'Generate Key'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Save Private Key View */}
                    {mode === 'save-private-key' && generatedKey && (
                        <div className="space-y-4">
                            <div className="p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                                <div className="flex gap-3">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-yellow-400">
                                            Save your private key now
                                        </h4>
                                        <p className="text-sm text-yellow-400/80 mt-1">
                                            This is the only time you can download this private key. It will not be stored on our servers.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Private Key
                                </label>
                                <textarea
                                    readOnly
                                    value={generatedKey.private_key}
                                    className="input resize-none font-mono text-xs bg-gray-800"
                                    rows={8}
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={copyPrivateKey}
                                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                                >
                                    {privateKeyCopied ? (
                                        <>
                                            <CheckIcon className="w-4 h-4 text-green-500" />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <ClipboardIcon className="w-4 h-4" />
                                            Copy
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={downloadPrivateKey}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                    Download .pem
                                </button>
                            </div>

                            <div className="pt-4 border-t border-gray-800">
                                <button
                                    type="button"
                                    onClick={handleSaveAndClose}
                                    className="w-full btn-secondary"
                                >
                                    I've saved my private key
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default CreateSSHKeyModal;
